import React, { useState, useEffect, useCallback } from 'react';
import { MarketingBanner } from '../MarketingBanner';
import type { BannerData } from '../MarketingBanner';
import type { WidgetProps } from './types';
import { getCachedBanners, getApiBase } from '../../utils/api';
import { getSessionId } from '../../hooks/useSearch';
import { useFontSizes } from '../../contexts/FontSizeContext';

async function trackImpression(bannerId: string, campaignId: string): Promise<void> {
  try {
    await fetch(`${getApiBase()}/marketing/track`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        sessionId: getSessionId(),
        type: 'impression',
        productId: bannerId,
        metadata: { campaignId },
      }),
    });
  } catch { /* silent */ }
}

export const BannerWidget: React.FC<WidgetProps> = ({ config }) => {
  const { heading } = useFontSizes();
  const [banners, setBanners] = useState<BannerData[]>([]);
  const [dismissed, setDismissed] = useState<Set<string>>(new Set());

  useEffect(() => {
    getCachedBanners().then((data) => {
      const limited = config.maxItems ? data.slice(0, config.maxItems) : data;
      setBanners(limited);
    });
  }, [config.maxItems]);

  const handleDismiss = useCallback((bannerId: string) => {
    setDismissed((prev) => new Set([...prev, bannerId]));
  }, []);

  const handleImpression = useCallback((bannerId: string, campaignId: string) => {
    trackImpression(bannerId, campaignId);
  }, []);

  const visible = banners.filter((b) => !dismissed.has(b.id));
  if (visible.length === 0) return null;

  return (
    <section className="mb-6">
      {config.title && (
        <h2 className={`${heading} font-semibold uppercase tracking-wider text-[#020202] mb-3`}>
          {config.title}
        </h2>
      )}
      <div className="space-y-2">
        {visible.map((banner) => (
          <MarketingBanner
            key={banner.id}
            banner={banner}
            onDismiss={handleDismiss}
            onImpression={handleImpression}
          />
        ))}
      </div>
    </section>
  );
};
